import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import LessonArtwork from "@/components/LessonArtwork";
import { useBooks } from "@/hooks/useBooks";
import { useLessons } from "@/hooks/useLessons";
import { useBookPurchases, useLessonPurchases } from "@/hooks/useUserPurchases";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { resolveLessonPrice, useLessonsPromoUntil } from "@/lib/lessonsPromo";

type UpsellContext = "cart" | "zemelapis" | "poros";

interface UpsellRecommendationsProps {
  /** Where the block is shown — drives which products get priority. */
  context: UpsellContext;
  /** Extra product ids to hide (e.g. the item just purchased). */
  excludeIds?: string[];
  /** Max number of cards. */
  limit?: number;
  className?: string;
  title?: string;
}

interface Rec {
  id: string;
  kind: "book" | "lesson" | "link";
  title: string;
  subtitle?: string | null;
  price: number | null;
  regularPrice?: number | null;
  cover_url?: string | null;
  author?: string | null;
  href?: string;
}

const UpsellRecommendations = ({
  context,
  excludeIds = [],
  limit = 3,
  className = "",
  title = "Taip pat Tau rekomenduojame",
}: UpsellRecommendationsProps) => {
  const { user } = useAuth();
  const { items, addItem, setCartOpen } = useCart();
  const { toast } = useToast();
  const { data: books = [] } = useBooks();
  const { data: lessons = [] } = useLessons();
  const { data: ownedBooks = [] } = useBookPurchases(user?.id);
  const { data: ownedLessons = [] } = useLessonPurchases(user?.id);
  const promoUntil = useLessonsPromoUntil();

  const cartIds = useMemo(() => items.map((i) => i.product.id), [items]);
  const cartTypes = useMemo(() => new Set(items.map((i) => i.product.type)), [items]);

  const recommendations = useMemo<Rec[]>(() => {
    const hidden = new Set([...cartIds, ...excludeIds]);

    const now = Date.now();
    const lessonRecs: Rec[] = lessons
      .filter((l) => !hidden.has(l.id) && !ownedLessons.includes(l.id) && !l.is_free)
      .filter((l) => !l.available_until || new Date(l.available_until).getTime() > now)
      .map((l) => {
        const pricing = resolveLessonPrice(l, promoUntil);
        return {
          id: l.id,
          kind: "lesson" as const,
          title: l.title,
          subtitle: l.duration,
          price: pricing.amount,
          regularPrice: pricing.onSale ? pricing.regularPrice : null,
          cover_url: l.cover_url,
        };
      });

    const bookRecs: Rec[] = books
      .filter((b) => !hidden.has(b.id) && !ownedBooks.includes(b.id))
      .map((b) => ({
        id: b.id,
        kind: "book" as const,
        title: b.title,
        subtitle: b.type === "ebook" ? "El. knyga" : "Knyga",
        price: b.price,
        regularPrice: b.compare_at_price ?? null,
        cover_url: b.cover_url,
        author: b.author,
      }));

    const zemelapisLinks: Rec[] = [];
    if (context === "zemelapis") {
      zemelapisLinks.push({
        id: "poros-zemelapis",
        kind: "link",
        title: "Poros Žemėlapis",
        subtitle: "Jums abiem — vienas bendras žemėlapis",
        price: 49.9,
        href: "/poros-zemelapis",
      });
    }

    // Priority order per context
    let ordered: Rec[];
    if (context === "zemelapis" || context === "poros") {
      ordered = [...zemelapisLinks, ...lessonRecs, ...bookRecs];
    } else if (cartTypes.has("zemelapis")) {
      ordered = [...lessonRecs, ...bookRecs];
    } else if (cartTypes.has("lesson") && !cartTypes.has("book")) {
      ordered = [...bookRecs, ...lessonRecs];
    } else {
      ordered = [...lessonRecs, ...bookRecs];
    }

    return ordered.slice(0, limit);
  }, [books, lessons, ownedBooks, ownedLessons, cartIds, excludeIds, cartTypes, context, limit, promoUntil]);

  if (recommendations.length === 0) return null;

  const handleAdd = (rec: Rec) => {
    addItem({
      id: rec.id,
      title: rec.title,
      price: rec.price ?? 0,
      regularPrice: rec.regularPrice ?? null,
      cover_url: rec.cover_url ?? null,
      type: rec.kind === "book" ? "book" : "lesson",
      author: rec.author ?? null,
    });
    toast({ title: "Pridėta į krepšelį", description: rec.title });
    if (context !== "cart") setCartOpen(true);
  };

  return (
    <section className={`border-t border-border pt-6 ${className}`}>
      <h2 className="text-sm tracking-[0.2em] uppercase text-muted-foreground mb-5">{title}</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {recommendations.map((rec) => (
          <div
            key={rec.id}
            className="flex gap-3 p-3 rounded-lg border border-border bg-background/60"
          >
            <div className="w-14 h-[72px] flex-shrink-0 rounded-md overflow-hidden bg-muted/30 border border-border">
              {rec.kind === "lesson" ? (
                <LessonArtwork title={rec.title} seed={rec.id} className="w-full h-full" />
              ) : rec.cover_url ? (
                <img
                  src={rec.cover_url}
                  alt={rec.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                  ✦
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0 flex flex-col">
              <p className="text-sm font-medium text-foreground leading-tight line-clamp-2">
                {rec.title}
              </p>
              {rec.subtitle && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{rec.subtitle}</p>
              )}

              <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-foreground">
                  {rec.price != null ? `€${Number(rec.price).toFixed(2)}` : ""}
                </span>
                {rec.kind === "link" ? (
                  <Button asChild size="sm" variant="outline" className="rounded-full h-7 px-3 text-xs">
                    <Link to={rec.href!}>Žiūrėti</Link>
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full h-7 px-3 text-xs"
                    onClick={() => handleAdd(rec)}
                  >
                    Į krepšelį
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default UpsellRecommendations;
